// Import necessary modules, components, and models from Vue and the project
import { Component, Vue, Prop, Watch } from "vue-facing-decorator";
import VsInput from "@/components/vs-input/vs-input.vue";
import VsResult from "@/components/vs-result/vs-result.vue";

import VsResultMap from "@/components/vs-result-map/vs-result-map.vue";

import FacetCategory from "@/components/face-category/facet-category.vue";
import ActiveFacetCategory from "@/components/active-facet-category/active-facet-category.vue";

// Import models and services
import { FilterItem, OpenSettings } from "@/models/solr";
import DatasetService from "../../services/dataset.service";
import { Suggestion, Dataset, SearchType } from "@/models/dataset";
import { FacetItem, FacetResults, OpenSearchResponse } from "@/models/headers";
import { ActiveFilterCategories } from "@/models/solr";
import { IPagination } from "@/models/pagination";
import PaginationTop from "@/components/PaginationTop.vue";
import PaginationBase from "@/components/PaginationBase.vue";
import FacetsComponent from "@/components/facets/FacetsComponent.vue";

import { OPEN_HOST, OPEN_CORE } from "@/constants";

// import TestLeafletMap from "@/components/TestLeafletMap.vue";
import HeroMap from "@/components/hero-map/HeroMap";

import { useGlobalStore } from "@/stores/store";
import { filter, forkJoin } from "rxjs"; // Import forkJoin

// const facetRelevance = "_score"; // OpenSearch index accepted value to filter by relevance
// const facetDate = "server_date_published"; // OpenSearch index accepted value to filter by date of publication in server
// const orderAsc = "asc"; // OpenSearch accepted value to order results ascending
// const orderDesc = "desc"; // OpenSearch accepted value to order results descending
const defaultSortByAttribute = "server_date_published"; // Default attribute to order results. OpenSearch index attribute to sort by. ONLY VALID: "server_date_published", "_score", or another field present in the OpenSearch index
const defaultSortByOrder = "desc"; // Default order of results. Attribute to indicate the order of the results. ONLY VALID: "asc" or "desc"

// Define the Vue component, its name, and child components
@Component({
    name: "MapViewComponent",
    components: {
        VsInput,
        VsResult,
        FacetCategory,
        ActiveFacetCategory,
        PaginationTop,
        PaginationBase,
        FacetsComponent,
        HeroMap,
        VsResultMap,
    },
})

// Export the default class for the component
export default class MapViewComponent extends Vue {
    // Define props passed from the parent component
    @Prop()
    display!: string; // Search display string
    @Prop()
    type!: string; // Search type
    // @Prop()
    // searchTerm!: string | Suggestion; // Search display string

    // Declare variables used in the component

    resultsPanelOpen = true;

    store = useGlobalStore;
    get results() {
        return this.store.results;
    }

    facets: FacetResults = new FacetResults(); // Object to hold facet results
    // searchTerm: string | Suggestion = ""; // The search term input
    searchTerm: string | Suggestion = this.display; // The search term input
    activeFilterCategories: ActiveFilterCategories = new ActiveFilterCategories(); // Active filter categories for search
    selectionBox: string | null = null; // Geometry for the selection rectangle
    pagination: IPagination = {
        // Pagination data for the results
        total: 0,
        perPage: 10,
        currentPage: 1,
        data: [],
    };
    loaded = false; // Boolean to track whether data has been loaded
    numFound!: number; // Number of results found

    // Define settings for sorting results:
    sortByAttribute = "server_date_published"; // Default attribute to order results. OpenSearch index attribute to sort by. ONLY VALID: "server_date_published", "_score", or another field present in the OpenSearch index
    sortByOrder = "desc"; // Default order of results. Attribute to indicate the order of the results. ONLY VALID: "asc" or "desc"
    // To easily list the possible values present un the index, go to https://catalog.geosphere.at/tethys-records/_search

    private bboxApplied = false;
    private lastAppliedBbox: string = ""; // a serialized version for comparison
    userEditedBbox = false;
    editingEnabled = false;
    

    @Watch("display")
    onDisplayChange(newDisplay: string): void {
        // console.log("Display prop changed:", newDisplay);
        this.onSearch(newDisplay);
    }

    // Define settings for the OpenSearch API (core and host information)
    private open: OpenSettings = {
        core: OPEN_CORE,
        host: OPEN_HOST, //"https://catalog.geosphere.at",
    };

    private error = "";

    // Computed property to get search term as string
    get stringSearchTerm(): string {
        // If searchTerm is a string, return it directly
        if (typeof this.searchTerm === "string") {
            return this.searchTerm;
            // If searchTerm is a Suggestion, return its value and type alias
        } else if (this.searchTerm instanceof Suggestion) {
            return this.searchTerm.value + " (" + this.getTypeAlias(this.searchTerm.type) + ")";
            // return this.searchTerm.value + " (" + this.searchTerm.type + ")";
            // Default to empty string
        } else {
            return "";
        }
    }

    /**
     * The alias for the search term type will be set depending on the name of the type.
     * This will allow to display the customised terms instead of the values currently used in the OpenSearch index.
     * TODO: This should be corrected directly in the index
     */
    getTypeAlias(type: string): string {
        switch (type) {
            case "author":
                return "creator";
            case "subjects":
                return "keyword";
            case "doctype":
                return "data type";
            default:
                return type;
        }
    }

    // Computed property to check if a search term is present
    get hasSearchTerm(): boolean {
        if (typeof this.searchTerm === "string" && this.searchTerm !== "") {
            return true;
        } else if (this.searchTerm instanceof Suggestion && this.searchTerm.value !== "") {
            return true;
        } else {
            return false;
        }
    }

    // Method to get enum key by enum value
    getEnumKeyByEnumValue<T extends { [index: string]: string }>(myEnum: T, enumValue: string): keyof T | null {
        const keys = Object.keys(myEnum).filter((x) => myEnum[x] == enumValue);
        return keys.length > 0 ? keys[0] : null;
        // return keys[0];
    }

    // Lifecycle hook: executed before the component is mounted
    beforeMount(): void {
        // console.log("beforeMount (search-view-component)");

        // Check if the display prop is not empty and the type prop is defined
        if (this.display != "" && this.type != undefined) {
            // Retrieve the enum key corresponding to the provided type prop
            const enumKey: "Title" | "Author" | "Subject" | "Doctype" | null = this.getEnumKeyByEnumValue(SearchType, this.type);

            // If a valid enum key is found, create a Suggestion object and trigger a search
            if (enumKey) {
                const suggestion = new Suggestion(this.display, "", SearchType[enumKey]);
                // const suggestion = new Suggestion(this.display, "" , SearchType[enumKey]);
                this.onSearch(suggestion);
            } else {
                // If no valid enum key is found, trigger a search with the display prop as a string
                this.onSearch(this.display);
            }
        }
        // If only the display prop is provided, trigger a search with it
        else if (this.display != "" && this.type == undefined) {
            this.onSearch(this.display);
        }
        // If neither display nor type props are provided, trigger a search with an empty string
        else {
            this.onSearch("");
        }
    }

    // Method to handle search errors
    private errorHandler(err: string): void {
        this.error = err;
    }

    // Method to handle pagination
    onMenuClick(page: number) {
        // console.log("onMenuClick");

        this.pagination.currentPage = page;
        const start = page * this.pagination.perPage - this.pagination.perPage;

        DatasetService.facetedSearch(
            this.searchTerm,
            this.activeFilterCategories,
            this.open.core,
            this.open.host,
            this.sortByAttribute,
            this.sortByOrder,
            start.toString(),
        ).subscribe({
            next: (res: OpenSearchResponse) => this.dataHandler(res),
            error: (error: string) => this.errorHandler(error),
        });
    }

    onDocumentHovered(bbox: { xmin: number; ymin: number; xmax: number; ymax: number } | null) {
        if (bbox != null) {
            const heroMap = this.$refs.heroMap as any;
            // console.log("onDocumentHovered: bbox =", bbox);
            // console.log("onDocumentHovered: heroMap =", heroMap);
            
            if (heroMap && heroMap.drawTemporaryPolygon) {
                heroMap.drawTemporaryPolygon(bbox);
            }
        } else {
            // console.log("onDocumentHovered: there is no bbox");
            
            const heroMap = this.$refs.heroMap as any;
            if (heroMap && heroMap.clearTemporaryPolygon) {
                heroMap.clearTemporaryPolygon();  
            }
        }
    }

    /**
     * Handles OpenSearch response and updates results, pagination and facet items.
     * @param res The OpenSearch response
     * @param filterItem Optional filter item to prioritize during facet updates
     * @param resetCategory Optional facet category name to reset (deactivates all items in that category)
     */
    /**
     * Handles the OpenSearch response and updates the results, pagination, and facet items.
     * @param res The OpenSearch response
     * @param filterItem Optional filter item to prioritize during facet updates
     * @param resetCategory Optional facet category name to reset (deactivates all items in that category)
     */
    private dataHandler(res: OpenSearchResponse, filterItem?: FacetItem, resetCategory?: string): void {
        // Update the global store with the search results
        this.store.results = res.hits.hits.map((hit) => hit._source);

        // Update the total number of results found
        this.numFound = res.hits.total.value;

        // Update pagination details
        this.pagination.total = res.hits.total.value;
        this.pagination.lastPage = Math.ceil(this.pagination.total / this.pagination.perPage);

        // Process facet aggregations if available in the response
        if (res.aggregations) {
            const facet_fields = res.aggregations;

            let prop: keyof typeof facet_fields;

            // Iterate through each facet category
            for (prop in facet_fields) {
                const facetCategory = facet_fields[prop];
                if (facetCategory.buckets) {
                    // Map the buckets to FacetItem objects
                    const facetItems = facetCategory.buckets.map((bucket) => new FacetItem(bucket.key, bucket.doc_count));

                    // Update or create facet values
                    let facetValues = facetItems.map((facetItem) => {
                        let rObj: FacetItem;

                        // Prioritize the provided filter item
                        if (filterItem?.val === facetItem.val) {
                            rObj = filterItem;
                        }
                        // Update existing facet items if they already exist
                        else if (this.facets[prop]?.some((e) => e.val === facetItem.val)) {
                            const indexOfFacetValue = this.facets[prop].findIndex((i) => i.val === facetItem.val);
                            rObj = this.facets[prop][indexOfFacetValue];
                            rObj.count = facetItem.count;

                            // Deactivate items in the reset category
                            if (prop === resetCategory) {
                                rObj.active = false;
                            }
                        }
                        // Create new facet items
                        else {
                            rObj = new FacetItem(facetItem.val, facetItem.count);
                        }

                        return rObj;
                    });

                    // Filter out facet items with a count of 0
                    facetValues = facetValues.filter((el) => el.count > 0);

                    // Update the facets object with the processed facet values
                    this.facets[prop] = facetValues;
                }
            }
        }
    }


    get bboxXMin(): string {
        return this.store.selectionBox ? this.store.selectionBox.xmin.toFixed(6) : "";
    }
    set bboxXMin(val: string) {
        this.updateStoreBbox("xmin", val);
    }
    
    get bboxYMin(): string {
        return this.store.selectionBox ? this.store.selectionBox.ymin.toFixed(6) : "";
    }
    set bboxYMin(val: string) {
        this.updateStoreBbox("ymin", val);
    }
    
    get bboxXMax(): string {
        return this.store.selectionBox ? this.store.selectionBox.xmax.toFixed(6) : "";
    }
    set bboxXMax(val: string) {
        this.updateStoreBbox("xmax", val);
    }
    
    get bboxYMax(): string {
        return this.store.selectionBox ? this.store.selectionBox.ymax.toFixed(6) : "";
    }
    set bboxYMax(val: string) {
        this.updateStoreBbox("ymax", val);
    }


    public selectionBoxIsNull() {
        return this.store.selectionBox === null;
    }
  
    get bboxHasChanges(): boolean {
        if (!this.store.selectionBox) return false;
    
        // console.log("lastAppliedBbox:", this.lastAppliedBbox);
        
        const last = this.lastAppliedBbox.split(",").map(parseFloat);
        const current = [
            this.store.selectionBox.xmin,
            this.store.selectionBox.ymin,
            this.store.selectionBox.xmax,
            this.store.selectionBox.ymax,
        ];
    
        return current.some((val, i) => isNaN(last[i]) || val !== last[i]);
    }    

    private serializeBbox(bbox: { xmin: number; ymin: number; xmax: number; ymax: number } | null): string {
        if (!bbox) return "";
        return `${bbox.xmin},${bbox.ymin},${bbox.xmax},${bbox.ymax}`;
    }

    /**
     * Update the corresponding property in store.selectionBox with parsed float value
     */
    private updateStoreBbox(key: "xmin" | "ymin" | "xmax" | "ymax", val: string) {
        const parsed = parseFloat(val);
        if (isNaN(parsed)) return;

        this.store.selectionBox = {
            ...(this.store.selectionBox || { xmin: 0, ymin: 0, xmax: 0, ymax: 0 }),
            [key]: parsed,
        };
    }

    onBboxInput() {
        this.userEditedBbox = true;
    }

    canApplyBoundingBox(): boolean {
        return this.bboxHasChanges && this.userEditedBbox;
    }

    // bboxInputs = {
    //     xmin: "",
    //     ymin: "",
    //     xmax: "",
    //     ymax: "",
    // };

    // bboxInputs = this.store.selectionBox; // Flag to control the visibility of bounding box inputs

    /**
     * Handles the selection made with a bounding box drawn on the map and applies it as a spatial filter to all search queries.
     * @param bbox Object with xmin, xmax, ymin, ymax
     */
    onBboxRectangleDrawn(bbox: { xmin: number; xmax: number; ymin: number; ymax: number }): void {
        this.activeFilterCategories["geometry"] = [`[${bbox.xmin},${bbox.ymin} TO ${bbox.xmax},${bbox.ymax}]`];
        this.store.selectionBox = bbox; // Store the bounding box geometry in the global store
        // console.log("onBboxRectangleDrawn store.selectionBox:", this.store.selectionBox);

        // Update input fields
        // this.bboxInputs = {
        //     xmin: bbox.xmin.toFixed(6),
        //     ymin: bbox.ymin.toFixed(6),
        //     xmax: bbox.xmax.toFixed(6),
        //     ymax: bbox.ymax.toFixed(6),
        // };

        this.onFilter();
    }


    public isValidBboxInput(): boolean {
        const bbox = this.store.selectionBox;
        if (!bbox) return false;

        const keys: Array<keyof typeof bbox> = ["xmin", "ymin", "xmax", "ymax"];

        const allValid = keys.every(
            (key) => typeof bbox[key] === "number" && !isNaN(bbox[key])
        );

        const inBounds = bbox.xmin < bbox.xmax && bbox.ymin < bbox.ymax;

        return allValid && inBounds;
    }

    /**
     * Applies the bounding box as a spatial filter to all search queries.
     * @param bbox Object with xmin, xmax, ymin, ymax
     */
    applyBoundingBox(): void {
        // this.bboxApplied = true;
    
        this.userEditedBbox = false;

        if (!this.isValidBboxInput()) {
            alert("Please enter valid coordinates: xmin < xmax, ymin < ymax.");
            return;
        }
    
        if (this.store.selectionBox) {
            const serialized = this.serializeBbox(this.store.selectionBox);
            this.lastAppliedBbox = serialized;
    
            this.onBboxRectangleDrawn(this.store.selectionBox);
            console.log("store.selectionBox:", this.store.selectionBox);
        }
        this.editingEnabled = false;
    }
    
    clearBoundingBox(callSource?: string): void {
        delete this.activeFilterCategories["geometry"];
        this.store.selectionBox = null; // Clear the selection rectangle geometry in the global store

        // this.bboxInputs = {
        //     xmin: "",
        //     ymin: "",
        //     xmax: "",
        //     ymax: "",
        // };
        
        this.editingEnabled = false;

        // ✅ Emit a signal to HeroMap to clear rectangle from map
        this.$emit("rectangle-drawn", null);

        if (callSource !== "fromTextualSearch") {
            // ✅ Call onFilter to update the results without the bounding box filter
            this.onFilter();  
        }
    }

    enableRectangleEditing(): void {
        const heroMapComponent = this.$refs.heroMap as any;
        if (heroMapComponent?.enableRectangleEditing) {
            heroMapComponent.enableRectangleEditing();
        } else {
            console.warn("HeroMap component or method not found.");
        }
    }
    
    onSearch(suggestion: Suggestion | string): void {
        console.log("onSearch MapViewComponent");

        this.clearBoundingBox("fromTextualSearch");

        // Reset active filter categories and facet results
        this.activeFilterCategories = new ActiveFilterCategories();
        this.facets = new FacetResults();
        this.searchTerm = suggestion;

        if (this.hasSearchTerm) {
            this.sortByAttribute = "_score";
            this.sortByOrder = "desc";
        } else {
            this.sortByAttribute = defaultSortByAttribute;
            this.sortByOrder = defaultSortByOrder;
        }

        // Perform both facetedSearch and bBoxFacetedSearch in parallel
        forkJoin({
            facetedSearch: DatasetService.facetedSearch(
                suggestion,
                this.activeFilterCategories,
                this.open.core,
                this.open.host,
                this.sortByAttribute,
                this.sortByOrder,
                undefined,
            ),
            bBoxFacetedSearch: DatasetService.bBoxFacetedSearch(
                suggestion, 
                this.activeFilterCategories,
                this.open.core,
                this.open.host,
                undefined
            ),
        }).subscribe({
            next: ({ facetedSearch, bBoxFacetedSearch }) => {
                // Handle facetedSearch results
                this.dataHandler(facetedSearch);
                this.pagination.currentPage = 1; // Reset to first page

                // Handle bBoxFacetedSearch results
                this.store.resultsForMap = bBoxFacetedSearch; // Store bounding box results
            },
            error: (error: string) => this.errorHandler(error),
        });
    }

    // Method to handle facet filtering
    // After implementing new faceting (Feb25) this method allows not only to ADD but also to REMOVE inidividual filter items
    // Previously, it was only possible to remove complete categories with the method onClearFacetCategory
    onFilter(facetItem?: FacetItem): void {
        console.log("onFilter");

        // Reset current page
        this.pagination.currentPage = 1;

        if (this.hasSearchTerm) {
            this.sortByAttribute = "_score";
            this.sortByOrder = "desc";
        } else {
            this.sortByAttribute = defaultSortByAttribute;
            this.sortByOrder = defaultSortByOrder;
        }

        if (facetItem) {
            console.log("facetItem:", facetItem);

            // Check if the facet item category already exists in the list of active filter categories
            if (!Object.prototype.hasOwnProperty.call(this.activeFilterCategories, facetItem.category)) {
                this.activeFilterCategories[facetItem.category] = new Array<string>();
            }

            // Check if the filter item value is already present in the active filter categories
            const filterIndex = this.activeFilterCategories[facetItem.category].indexOf(facetItem.val);
            if (filterIndex === -1) {
                // Add filter item value to active filter categories
                this.activeFilterCategories[facetItem.category].push(facetItem.val);
            } else {
                // Remove filter item value from active filter categories
                this.activeFilterCategories[facetItem.category].splice(filterIndex, 1);
            }
        }

        // Perform both facetedSearch and bBoxFacetedSearch in parallel
        forkJoin({
            facetedSearch: DatasetService.facetedSearch(
                this.searchTerm,
                this.activeFilterCategories,
                this.open.core,
                this.open.host,
                this.sortByAttribute,
                this.sortByOrder,
                undefined,
            ),
            bBoxFacetedSearch: DatasetService.bBoxFacetedSearch(this.searchTerm, this.activeFilterCategories, this.open.core, this.open.host, undefined),
        }).subscribe({
            next: ({ facetedSearch, bBoxFacetedSearch }) => {
                // Handle facetedSearch results
                this.dataHandler(facetedSearch, facetItem);

                // Handle bBoxFacetedSearch results
                this.store.resultsForMap = bBoxFacetedSearch; // Update bounding box data
            },
            error: (error: string) => this.errorHandler(error),
        });
    }

    // Method to handle sorting criteria changes
    onChangeOrdering([sortingAttribute, sortingOrder]: [string, string]): void {
        // console.log(".onChangeOrdering:" + sortingAttribute + " / " + sortingOrder);
        // console.log("sortingAttribute:", sortingAttribute);
        // console.log("sortingOrder:", sortingOrder);

        // Reset current page
        this.pagination.currentPage = 1;

        if (sortingAttribute === "relevance") {
            this.sortByAttribute = "_score";
        } else {
            this.sortByAttribute = defaultSortByAttribute;
        }

        if (sortingOrder === "ascending") {
            this.sortByOrder = "asc";
        } else {
            this.sortByOrder = defaultSortByOrder;
        }

        DatasetService.facetedSearch(
            this.searchTerm,
            this.activeFilterCategories,
            this.open.core,
            this.open.host,
            this.sortByAttribute,
            this.sortByOrder,
            undefined,
        ).subscribe({
            next: (res: OpenSearchResponse) => {
                this.dataHandler(res);
            },
            error: (error: string) => this.errorHandler(error),
        });
    }

    // Method to clear facet category filter
    onClearFacetCategory(categoryName: string): void {
        // console.log("onClearFacetCategory");

        if (this.hasSearchTerm) {
            this.sortByAttribute = "_score";
            this.sortByOrder = "desc";
        } else {
            this.sortByAttribute = defaultSortByAttribute;
            this.sortByOrder = defaultSortByOrder;
        }

        if (categoryName === undefined) {
            // console.log("Clear all facet categories");
            this.store.selectionBox = null; // Clear the selection rectangle geometry in the global store
            this.activeFilterCategories = {};
        } else {
            // console.log("Clear facet category:", categoryName);
            delete this.activeFilterCategories[categoryName];
            if (categoryName === "geometry") {
                this.store.selectionBox = null; // Clear the selection rectangle geometry in the global store
            }
        }

        // Perform both facetedSearch and bBoxFacetedSearch in parallel
        forkJoin({
            facetedSearch: DatasetService.facetedSearch(
                this.searchTerm,
                this.activeFilterCategories,
                this.open.core,
                this.open.host,
                this.sortByAttribute,
                this.sortByOrder,
                undefined,
            ),
            bBoxFacetedSearch: DatasetService.bBoxFacetedSearch(this.searchTerm, this.activeFilterCategories, this.open.core, this.open.host, undefined),
        }).subscribe({
            next: ({ facetedSearch, bBoxFacetedSearch }) => {
                // Handle facetedSearch results
                this.pagination.currentPage = 1;
                this.dataHandler(facetedSearch, undefined, categoryName);

                // Handle bBoxFacetedSearch results
                this.store.resultsForMap = bBoxFacetedSearch; // Update bounding box data
            },
            error: (error: string) => this.errorHandler(error),
        });
    }

    // Method to scroll to the results section
    // scrollToResults(): void {
    //     const resultsElement = this.$el.querySelector('.results');
    //     if (resultsElement) {
    //         resultsElement.scrollIntoView({ behavior: 'smooth' });
    //     }
    // }

    scrollToTop(): void {
        setTimeout(() => {
            window.scrollTo({
                top: 0,
                behavior: "smooth", // Smooth scroll to the top
            });
        }, 50); // Delay to allow the DOM to update. Useful actually?
    }
}
